import { useEffect, useMemo, useRef, useState } from "react";
import { Lock, Star, Heart, Snowflake, Target, X, Play, RotateCcw, Share2, ChevronRight, Crown, Hammer, Shuffle, Plus, CalendarHeart, Coins } from "lucide-react";
import { spriteUrl, NAMES } from "@/lib/candySprites";
import { UserAvatar } from "@/components/UserBits";

const CHAPTERS = ["Rosengarten", "Kristallsee", "Goldpalast", "Sternennacht"];
const ROMAN = ["I", "II", "III", "IV"];
const STEP = 96;

export const Stars = ({ n, size = "w-4 h-4", gap = "gap-0.5", testId }) => (
  <span className={`inline-flex ${gap}`} data-testid={testId}>
    {[0, 1, 2].map((i) => <Star key={i} className={`${size} ${i < n ? "text-amber-300 drop-shadow-[0_0_6px_rgba(252,211,77,.7)]" : "text-white/20"}`} fill="currentColor" strokeWidth={0} />)}
  </span>
);

export function useIcons() {
  return useMemo(() => Array.from({ length: 6 }, (_, c) => spriteUrl(c, null, 88)), []);
}

export function goalText(lv) {
  if (lv.kind === "collect") return "Sammle die angezeigten Süßigkeiten";
  if (lv.kind === "ice") return "Befreie alle Felder vom Eis";
  return `Erreiche ${lv.stars[0].toLocaleString("de-DE")} Punkte`;
}

export function Lives({ lives, max, nextIn, onRefill }) {
  const [left, setLeft] = useState(nextIn);
  useEffect(() => { setLeft(nextIn); }, [nextIn]);
  useEffect(() => { if (lives >= max) return; const t = setInterval(() => setLeft((v) => Math.max(0, v - 1)), 1000); return () => clearInterval(t); }, [lives, max]);
  useEffect(() => { if (lives < max && left === 0 && nextIn > 0) onRefill?.(); }, [left, lives, max, nextIn, onRefill]);
  const mm = String(Math.floor(left / 60)).padStart(2, "0"), ss = String(left % 60).padStart(2, "0");
  return (
    <div className="cz-glass rounded-full h-10 pl-2 pr-3.5 inline-flex items-center gap-2" data-testid="candy-lives">
      <span className="relative w-7 h-7 rounded-full bg-gradient-to-b from-rose-400 to-rose-700 flex items-center justify-center shadow-[0_4px_12px_rgba(244,63,94,.5)]">
        <Heart className="w-3.5 h-3.5 text-white" fill="currentColor" />
      </span>
      <span className="font-extrabold tabular-nums text-white" data-testid="candy-lives-count">{lives}</span>
      <span className="text-[11px] font-semibold text-white/60 tabular-nums whitespace-nowrap">{lives >= max ? "voll" : `${mm}:${ss}`}</span>
    </div>
  );
}

function FriendStack({ list, n }) {
  if (!list?.length) return null;
  return (
    <span className="absolute left-full ml-3 top-1/2 -translate-y-1/2 flex items-center" data-testid={`candy-node-${n}-friends`}>
      {list.slice(0, 3).map((f, i) => (
        <span key={f.user_id} title={f.name} className={`block rounded-full ring-2 ring-[#2a0a1f] ${i ? "-ml-3" : ""}`} style={{ zIndex: 3 - i }}>
          <UserAvatar userId={f.user_id} name={f.name} picture={f.picture} size="w-8 h-8" text="text-[11px]" noStory />
        </span>
      ))}
      {list.length > 3 && <span className="-ml-2 h-6 px-1.5 rounded-full bg-white/15 text-[10px] font-extrabold text-white flex items-center">+{list.length - 3}</span>}
    </span>
  );
}

function Node({ lv, stars, state, onPick, me, pos, friends }) {
  const cur = state === "current", lock = state === "locked";
  return (
    <button onClick={() => !lock && onPick(lv)} disabled={lock} data-testid={`candy-node-${lv.n}`} data-state={state}
      className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center group" style={{ left: `${pos.x}%`, top: pos.y }}>
      {cur && me && (
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 cz-float"><span className="block rounded-full ring-2 ring-amber-200 shadow-[0_6px_20px_rgba(0,0,0,.5)]"><UserAvatar userId={me.user_id} name={me.name} picture={me.picture} size="w-10 h-10" text="text-sm" noStory /></span></span>
      )}
      {cur && <span className="absolute w-[92px] h-[92px] rounded-full cz-halo" aria-hidden />}
      <FriendStack list={friends} n={lv.n} />
      <span className={`relative rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-105 ${cur ? "w-[74px] h-[74px] cz-node-cur" : "w-[60px] h-[60px]"} ${lock ? "cz-node-lock" : cur ? "" : "cz-node-done"}`}>
        {lock ? <Lock className="w-5 h-5 text-white/40" /> : <span className={`cz-serif font-extrabold italic ${cur ? "text-[28px] text-[#3b0a1f]" : "text-[22px] text-[#3b0a1f]"}`}>{lv.n}</span>}
      </span>
      {!lock && stars > 0 && <span className="mt-1"><Stars n={stars} size="w-3.5 h-3.5" /></span>}
    </button>
  );
}

export function LevelMap({ levels, progress, unlocked, me, onPick, friends = [] }) {
  const ref = useRef(null);
  const H = levels.length * STEP + 160;
  const pos = (i) => ({ x: 50 + Math.sin(i * 0.85) * 28, y: H - 110 - i * STEP });
  const path = useMemo(() => {
    let d = "";
    levels.forEach((_, i) => {
      const p = pos(i), x = (p.x / 100) * 360;
      if (!i) d = `M ${x} ${p.y}`;
      else { const q = pos(i - 1), qx = (q.x / 100) * 360; d += ` C ${qx} ${q.y - STEP / 2}, ${x} ${p.y + STEP / 2}, ${x} ${p.y}`; }
    });
    return d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levels.length]);
  useEffect(() => {
    const el = ref.current?.querySelector(`[data-testid="candy-node-${unlocked}"]`);
    el?.scrollIntoView({ block: "center" });
  }, [unlocked]);
  const doneY = pos(Math.max(0, unlocked - 1)).y;
  const byLevel = useMemo(() => friends.reduce((m, f) => ({ ...m, [f.level]: [...(m[f.level] || []), f] }), {}), [friends]);
  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[420px]" style={{ height: H }} data-testid="candy-map">
      {CHAPTERS.map((name, i) => (
        <div key={name} className="absolute left-0 right-0 flex items-center gap-3 px-6" style={{ top: pos(i * 15).y + 54 }} data-testid={`candy-chapter-${i + 1}`}>
          <span className="flex-1 h-px bg-gradient-to-r from-transparent to-amber-200/40" />
          <span className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-amber-100/80 whitespace-nowrap">Kapitel {ROMAN[i]} · <span className="cz-serif normal-case tracking-normal italic text-sm text-amber-200">{name}</span></span>
          <span className="flex-1 h-px bg-gradient-to-l from-transparent to-amber-200/40" />
        </div>
      ))}
      <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 360 ${H}`} preserveAspectRatio="none" aria-hidden>
        <defs>
          <linearGradient id="czGold" x1="0" x2="1"><stop offset="0" stopColor="#ffe9b8" /><stop offset="1" stopColor="#d79a3a" /></linearGradient>
          <clipPath id="czDone"><rect x="0" y={doneY} width="360" height={H} /></clipPath>
        </defs>
        <path d={path} fill="none" stroke="rgba(255,255,255,.09)" strokeWidth="14" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <path d={path} fill="none" stroke="rgba(255,233,184,.25)" strokeWidth="2" strokeDasharray="2 10" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <path d={path} fill="none" stroke="url(#czGold)" strokeWidth="4" strokeLinecap="round" clipPath="url(#czDone)" vectorEffect="non-scaling-stroke" style={{ filter: "drop-shadow(0 0 6px rgba(255,200,120,.6))" }} />
      </svg>
      {levels.map((lv, i) => (
        <Node key={lv.n} lv={lv} pos={pos(i)} me={me} friends={byLevel[lv.n]} stars={progress[String(lv.n)] || 0} onPick={onPick}
          state={lv.n < unlocked || (progress[String(lv.n)] || 0) > 0 ? (lv.n === unlocked ? "current" : "done") : lv.n === unlocked ? "current" : "locked"} />
      ))}
    </div>
  );
}

const Goals = ({ lv, icons, collected = {}, iceLeft, compact }) => {
  if (lv.kind === "collect") return (
    <div className="flex gap-2">
      {Object.entries(lv.collect).map(([c, n]) => {
        const left = Math.max(0, n - (collected[c] || 0));
        return (
          <span key={c} className={`relative inline-flex items-center gap-1.5 rounded-2xl ${compact ? "px-1.5 py-1" : "px-3 py-2 bg-white/5 ring-1 ring-white/10"}`} data-testid={`candy-goal-${c}`} title={NAMES[c]}>
            <img src={icons[c]} alt={NAMES[c]} className={compact ? "w-8 h-8" : "w-10 h-10"} />
            {left ? <span className="font-extrabold tabular-nums text-white">{left}</span> : <span className="w-5 h-5 rounded-full bg-emerald-400 text-[#06281b] text-xs font-black flex items-center justify-center">✓</span>}
          </span>
        );
      })}
    </div>
  );
  if (lv.kind === "ice") return (
    <span className="inline-flex items-center gap-1.5 text-white" data-testid="candy-goal-ice"><span className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-100/80 to-sky-300/40 ring-1 ring-white/60 flex items-center justify-center"><Snowflake className="w-5 h-5 text-sky-900/70" /></span><span className="font-extrabold tabular-nums">{iceLeft ?? lv.ice.length}</span></span>
  );
  return <span className="inline-flex items-center gap-1.5 text-white" data-testid="candy-goal-score"><Target className="w-6 h-6 text-amber-200" /><span className="font-extrabold tabular-nums text-sm">{lv.stars[0].toLocaleString("de-DE")}</span></span>;
};

export function LevelIntro({ lv, icons, best, stars, lives, busy, onPlay, onClose }) {
  return (
    <div className="fixed inset-0 z-[85] bg-black/60 backdrop-blur-md flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="candy-intro">
      <div className="cz-card w-full max-w-sm rounded-[32px] p-6 text-center cz-rise" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Schließen" className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 text-white/80 flex items-center justify-center" data-testid="candy-intro-close"><X className="w-4 h-4" /></button>
        <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-amber-100/70">{lv.daily ? "Heute" : "Level"}</p>
        <p className={`cz-serif cz-gold-text font-extrabold italic leading-none mt-1 ${lv.daily ? "text-4xl" : "text-6xl"}`} data-testid="candy-intro-level">{lv.daily ? "Tages-Level" : lv.n}</p>
        {lv.daily && <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-200/15 ring-1 ring-amber-200/40 px-3 py-1 text-xs font-extrabold text-amber-100" data-testid="candy-intro-double">✦ Doppelte Sterne</p>}
        <div className="mt-2 flex justify-center"><Stars n={stars} size="w-6 h-6" gap="gap-1.5" /></div>
        <div className="mt-5 rounded-3xl bg-white/5 ring-1 ring-white/10 p-4 space-y-3">
          <p className="text-sm font-semibold text-white/85">{goalText(lv)}</p>
          <div className="flex justify-center"><Goals lv={lv} icons={icons} /></div>
          <p className="text-xs text-white/55">{lv.moves} Züge · ★ {lv.stars[0].toLocaleString("de-DE")} · ★★ {lv.stars[1].toLocaleString("de-DE")} · ★★★ {lv.stars[2].toLocaleString("de-DE")}</p>
          {best > 0 && <p className="text-xs text-amber-200/80">Deine Bestleistung: {best.toLocaleString("de-DE")}</p>}
        </div>
        <button onClick={onPlay} disabled={busy || lives <= 0} data-testid="candy-intro-play" className="cz-btn mt-5 w-full h-14 rounded-full font-extrabold text-lg inline-flex items-center justify-center gap-2 disabled:opacity-50">
          <Play className="w-5 h-5" fill="currentColor" /> {lives > 0 ? "Spielen" : "Keine Leben"}
        </button>
        <p className="mt-2 text-[11px] text-white/45">Ein Leben wird eingesetzt – bei einem Sieg bekommst du es zurück.</p>
      </div>
    </div>
  );
}

export function Hud({ lv, icons, hud, onQuit }) {
  const max = lv.stars[2] * 1.08, pct = Math.min(100, (hud.score / max) * 100);
  const got = lv.stars.filter((s) => hud.score >= s).length;
  return (
    <div className="w-full max-w-[540px] mx-auto px-3 pt-[calc(env(safe-area-inset-top)+10px)] space-y-2.5" data-testid="candy-hud">
      <div className="flex items-center justify-between">
        <button onClick={onQuit} aria-label="Level verlassen" data-testid="candy-quit" className="w-10 h-10 rounded-full cz-glass text-white/85 flex items-center justify-center"><X className="w-5 h-5" /></button>
        <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-amber-100/80">{lv.daily ? "Tages-Level" : <>Level <span className="cz-serif italic text-base normal-case tracking-normal text-amber-200">{lv.n}</span></>}</p>
        <span className="w-10" />
      </div>
      <div className="cz-glass rounded-[26px] p-3 flex items-center gap-3">
        <div className="shrink-0 w-[72px] h-[72px] rounded-[20px] cz-moves flex flex-col items-center justify-center" data-testid="candy-moves">
          <span className="cz-serif text-[34px] leading-none font-extrabold italic text-[#3b0a1f] tabular-nums">{hud.moves}</span>
          <span className="text-[9px] font-extrabold uppercase tracking-widest text-[#3b0a1f]/70">Züge</span>
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Goals lv={lv} icons={icons} collected={hud.collected} iceLeft={hud.iceLeft} compact />
            <span className="cz-serif italic font-extrabold text-xl text-white tabular-nums" data-testid="candy-score">{hud.score.toLocaleString("de-DE")}</span>
          </div>
          <div className="relative h-3 rounded-full bg-black/35 ring-1 ring-white/10">
            <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-rose-400 via-amber-300 to-amber-100 transition-[width] duration-500" style={{ width: `${pct}%` }} />
            {lv.stars.map((s, i) => (
              <Star key={i} className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 transition-transform ${i < got ? "text-amber-300 scale-125 drop-shadow-[0_0_6px_rgba(252,211,77,.9)]" : "text-white/35"}`} style={{ left: `${(s / max) * 100}%` }} fill="currentColor" strokeWidth={0} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Result({ res, lv, onNext, onRetry, onMap, onShare, lives }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!res.won) return;
    const ts = [0, 1, 2].filter((i) => i < res.stars).map((i) => setTimeout(() => { setShown(i + 1); import("@/lib/candySfx").then((m) => m.czStar(i)); }, 500 + i * 450));
    return () => ts.forEach(clearTimeout);
  }, [res]);
  return (
    <div className="fixed inset-0 z-[90] bg-black/65 backdrop-blur-md flex items-center justify-center p-4" data-testid="candy-result">
      <div className="cz-card w-full max-w-sm rounded-[32px] p-6 text-center cz-rise">
        <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-amber-100/70">{lv.daily ? "Tages-Level" : `Level ${lv.n}`}</p>
        <h2 className="hero-plain cz-serif text-4xl font-extrabold italic mt-1 text-white" data-testid="candy-result-title">{res.won ? "Wundervoll!" : "Knapp vorbei"}</h2>
        {res.won ? (
          <div className="mt-4 flex justify-center gap-2" data-testid="candy-result-stars">
            {[0, 1, 2].map((i) => (
              <Star key={i} className={`w-14 h-14 ${i === 1 ? "-mt-3" : ""} ${i < shown ? "text-amber-300 cz-star-pop drop-shadow-[0_0_16px_rgba(252,211,77,.8)]" : "text-white/15"}`} fill="currentColor" strokeWidth={0} />
            ))}
          </div>
        ) : <p className="mt-3 text-white/70 text-sm">Das Ziel wurde nicht ganz erreicht. Versuch es gleich noch einmal!</p>}
        <p className="mt-4 cz-serif italic font-extrabold text-3xl cz-gold-text tabular-nums" data-testid="candy-result-score">{res.score.toLocaleString("de-DE")}</p>
        {res.won && (
          <p className="mt-1 text-sm text-white/80" data-testid="candy-result-points">
            {res.points > 0 ? <>+<b className="text-amber-200">{res.points}</b> Community-Punkte</> : res.today_points >= res.daily_cap ? "Tageslimit an Punkten erreicht" : "Keine neuen Sterne – keine Punkte"}
            {res.new_stars > 0 && <span className="text-white/55"> · {res.new_stars} neue{res.new_stars === 1 ? "r" : ""} Stern{res.new_stars === 1 ? "" : "e"}</span>}
          </p>
        )}
        <div className="mt-6 space-y-2">
          {res.daily && res.daily_rank && <p className="text-sm text-amber-100" data-testid="candy-result-rank">Platz <b className="text-amber-200">{res.daily_rank}</b> in der heutigen Bestenliste</p>}
          {res.won && !lv.daily && lv.n < 60 && <button onClick={onNext} data-testid="candy-result-next" className="cz-btn w-full h-13 py-3.5 rounded-full font-extrabold inline-flex items-center justify-center gap-1.5">Nächstes Level <ChevronRight className="w-5 h-5" /></button>}
          <div className="grid grid-cols-2 gap-2">
            <button onClick={onRetry} disabled={lives <= 0} data-testid="candy-result-retry" className="h-12 rounded-full bg-white/10 ring-1 ring-white/15 text-white font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40"><RotateCcw className="w-4 h-4" /> Nochmal</button>
            <button onClick={onShare} data-testid="candy-result-share" className="h-12 rounded-full bg-white/10 ring-1 ring-white/15 text-white font-bold text-sm inline-flex items-center justify-center gap-1.5"><Share2 className="w-4 h-4" /> Herausfordern</button>
          </div>
          <button onClick={onMap} data-testid="candy-result-map" className="w-full h-10 text-white/60 text-sm font-semibold">Zur Level-Karte</button>
        </div>
      </div>
    </div>
  );
}

export function Board({ title, rows, testId }) {
  return (
    <section className="cz-glass rounded-[28px] p-5" data-testid={testId}>
      <h3 className="cz-serif italic font-extrabold text-xl text-white flex items-center gap-2"><Crown className="w-5 h-5 text-amber-300" fill="currentColor" /> {title}</h3>
      {rows.length ? (
        <ol className="mt-3 space-y-2">
          {rows.map((r, i) => (
            <li key={r.user.user_id} className="flex items-center gap-3">
              <span className={`w-6 text-center cz-serif italic font-extrabold ${i < 3 ? "text-amber-200" : "text-white/50"}`}>{i + 1}</span>
              <UserAvatar userId={r.user.user_id} name={r.user.name} picture={r.user.picture} size="w-9 h-9" text="text-xs" noStory />
              <span className="flex-1 min-w-0 truncate text-white font-semibold text-sm">{r.user.name}</span>
              <span className="text-xs text-white/55">Lvl {r.level}</span>
              <span className="inline-flex items-center gap-1 text-amber-200 font-extrabold text-sm tabular-nums"><Star className="w-3.5 h-3.5" fill="currentColor" strokeWidth={0} />{r.stars}</span>
            </li>
          ))}
        </ol>
      ) : <p className="mt-3 text-sm text-white/50">Noch niemand dabei – sei die Erste oder der Erste!</p>}
    </section>
  );
}


const BOOSTS = [["hammer", Hammer, "Hammer"], ["shuffle", Shuffle, "Mischen"]];

export function Boosters({ cfg, used, points, hammerOn, onHammer, onShuffle }) {
  const left = cfg.per_try - used;
  return (
    <div className="w-full max-w-[540px] mx-auto px-3 pb-[calc(env(safe-area-inset-bottom)+12px)] flex items-center gap-2" data-testid="candy-boosters">
      {BOOSTS.map(([k, Icon, label]) => {
        const price = cfg[k], on = k === "hammer" && hammerOn, dis = !on && (left <= 0 || points < price);
        return (
          <button key={k} onClick={k === "hammer" ? onHammer : onShuffle} disabled={dis} data-testid={`candy-boost-${k}`} data-active={on || undefined}
            className={`flex-1 h-14 rounded-2xl inline-flex items-center justify-center gap-2 font-bold text-sm transition-transform active:scale-95 disabled:opacity-40 ${on ? "cz-btn" : "cz-glass text-white"}`}>
            <Icon className="w-5 h-5" /> <span className="flex flex-col items-start leading-tight"><span>{on ? "Abbrechen" : label}</span><span className={`text-[10px] font-extrabold inline-flex items-center gap-0.5 ${on ? "text-[#3b0a1f]/70" : "text-amber-200"}`}><Coins className="w-3 h-3" />{price}</span></span>
          </button>
        );
      })}
      <div className="shrink-0 text-right leading-tight px-1">
        <p className="text-[10px] uppercase tracking-widest text-white/45 font-bold">Hilfen</p>
        <p className="text-sm font-extrabold text-white tabular-nums" data-testid="candy-boosts-left">{Math.max(0, left)}/{cfg.per_try}</p>
      </div>
    </div>
  );
}

export function MoreMoves({ price, points, canBuy, busy, onBuy, onGiveUp }) {
  return (
    <div className="fixed inset-0 z-[90] bg-black/65 backdrop-blur-md flex items-center justify-center p-4" data-testid="candy-more-moves">
      <div className="cz-card w-full max-w-sm rounded-[32px] p-6 text-center cz-rise">
        <span className="mx-auto w-16 h-16 rounded-full cz-moves flex items-center justify-center"><Plus className="w-8 h-8 text-[#3b0a1f]" strokeWidth={3} /></span>
        <h2 className="hero-plain cz-serif text-3xl font-extrabold italic mt-3 text-white">So knapp!</h2>
        <p className="mt-2 text-sm text-white/75">Deine Züge sind aufgebraucht. Mit 5 weiteren Zügen schaffst du es vielleicht noch.</p>
        <button onClick={onBuy} disabled={busy || !canBuy || points < price} data-testid="candy-more-moves-buy" className="cz-btn mt-5 w-full h-14 rounded-full font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-50">
          +5 Züge · <Coins className="w-4 h-4" /> {price} Punkte
        </button>
        <p className="mt-2 text-[11px] text-white/50" data-testid="candy-more-moves-info">{!canBuy ? "Für diesen Versuch sind keine Hilfsmittel mehr übrig." : points < price ? `Du hast nur ${points} Punkte.` : `Dein Guthaben: ${points.toLocaleString("de-DE")} Punkte`}</p>
        <button onClick={onGiveUp} disabled={busy} data-testid="candy-more-moves-giveup" className="mt-3 w-full h-11 text-white/65 text-sm font-semibold">Aufgeben</button>
      </div>
    </div>
  );
}

function DailyFriends({ list, onShare }) {
  return (
    <div className="relative mt-4 rounded-2xl bg-black/20 ring-1 ring-white/10 p-3" data-testid="candy-daily-friends">
      <p className="text-[10px] font-extrabold uppercase tracking-[0.25em] text-amber-100/70">Deine Freunde heute</p>
      {list.length ? (
        <ul className="mt-2 space-y-1.5">
          {list.map((r) => (
            <li key={r.user.user_id} className="flex items-center gap-2.5 text-sm" data-testid={`candy-daily-friend-${r.user.user_id}`}>
              <UserAvatar userId={r.user.user_id} name={r.user.name} picture={r.user.picture} size="w-7 h-7" text="text-[10px]" noStory />
              <span className="flex-1 min-w-0 truncate text-white/90">{r.user.name.split(" ")[0]}</span>
              <Stars n={r.stars} size="w-3 h-3" />
              <span className="font-extrabold tabular-nums text-amber-200 text-xs">{r.score.toLocaleString("de-DE")} Pkt.</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-1.5 flex items-center gap-2">
          <p className="flex-1 text-xs text-white/60">Noch keiner deiner Freunde hat heute gespielt.</p>
          <button onClick={onShare} data-testid="candy-daily-friends-share" className="shrink-0 h-8 px-3 rounded-full bg-white/10 ring-1 ring-white/20 text-white text-xs font-bold inline-flex items-center gap-1"><Share2 className="w-3.5 h-3.5" /> Herausfordern</button>
        </div>
      )}
    </div>
  );
}

export function DailyCard({ lv, icons, best, top, prizes, onPlay, friends = [], onShare }) {
  return (
    <section className="relative overflow-hidden rounded-[30px] p-5 cz-daily" data-testid="candy-daily-level">
      <span className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-amber-200/10" aria-hidden />
      <div className="relative flex items-start gap-3">
        <span className="w-12 h-12 shrink-0 rounded-2xl cz-moves flex items-center justify-center"><CalendarHeart className="w-6 h-6 text-[#3b0a1f]" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.28em] text-amber-100/75">Heute · {new Date(`${lv.day}T12:00:00`).toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })}</p>
          <h3 className="cz-serif italic font-extrabold text-2xl text-white leading-tight">Tages-Level</h3>
          <p className="text-xs text-white/70 mt-0.5">{goalText(lv)} · {lv.moves} Züge · <b className="text-amber-200">doppelte Sterne</b></p>
        </div>
      </div>
      <div className="relative mt-3 flex items-center justify-between gap-3">
        <Goals lv={lv} icons={icons} compact />
        <span className="text-right" data-testid="candy-daily-best"><Stars n={best.stars} size="w-4 h-4" /><span className="block text-[11px] text-white/60 tabular-nums">{best.score ? `Bestwert ${best.score.toLocaleString("de-DE")}` : "Noch nicht gespielt"}</span></span>
      </div>
      {top.length > 0 && (
        <ol className="relative mt-3 space-y-1.5" data-testid="candy-daily-top">
          {top.slice(0, 5).map((r, i) => (
            <li key={r.user.user_id} className="flex items-center gap-2.5 text-sm">
              <span className={`w-5 text-center cz-serif italic font-extrabold ${i < 3 ? "text-amber-200" : "text-white/45"}`}>{i + 1}</span>
              <UserAvatar userId={r.user.user_id} name={r.user.name} picture={r.user.picture} size="w-7 h-7" text="text-[10px]" noStory />
              <span className="flex-1 min-w-0 truncate text-white/90">{r.user.name}</span>
              <span className="font-extrabold tabular-nums text-white">{r.score.toLocaleString("de-DE")}</span>
            </li>
          ))}
        </ol>
      )}
      <DailyFriends list={friends} onShare={onShare} />
      <p className="relative mt-3 text-[11px] text-white/55">Preise morgen früh: Platz 1 <b className="text-amber-200">+{prizes[0]}</b> · Platz 2 <b className="text-amber-200">+{prizes[1]}</b> · Platz 3 <b className="text-amber-200">+{prizes[2]}</b> Punkte</p>
      <button onClick={onPlay} data-testid="candy-daily-play" className="cz-btn relative mt-4 w-full h-12 rounded-full font-extrabold inline-flex items-center justify-center gap-2"><Play className="w-4 h-4" fill="currentColor" /> Tages-Level spielen</button>
    </section>
  );
}
